/**
 * Add events on resize.
 * If `element` is provided, `ResizeObserver` will be used (first callback will not be fired).
 * Otherwise, viewport callbacks will be used.
 *
 * @example
 *
 * const handler = onResize({
 *   onResize: () => console.log('resize'),
 *   element: document.getElementById('app')!,
 *   viewportTarget: 'any',
 *   hasBothEvents: true,   // trace both element and viewport sizes
 *   resizeDebounce: 100,
 * });
 *
 * // resize with timeout called twice, but `onResize` will be called only once
 * handler.debounceResize();
 * handler.debounceResize();
 *
 * // resize without timeout
 * handler.resize();
 *
 * // remove listeners
 * handler.remove();
 */
export function onResize({ onResize: handleResize, element, viewportTarget = 'any', hasBothEvents = false, resizeDebounce = 0, }) {
    let timeout;
    let resizeObserver;
    let isFirstResizeObserverCallback = true;
    let viewportCallback;
    const debounceResize = (props, delay) => {
        if (timeout) {
            clearTimeout(timeout);
        }
        timeout = setTimeout(() => handleResize(props !== null && props !== void 0 ? props : { trigger: 'unknown' }), delay !== null && delay !== void 0 ? delay : resizeDebounce);
    };
    if (element &&
        (element instanceof Element || Array.isArray(element)) &&
        'ResizeObserver' in window) {
        resizeObserver = new ResizeObserver(() => {
            if (isFirstResizeObserverCallback) {
                isFirstResizeObserverCallback = false;
                return;
            }
            debounceResize({ trigger: 'ResizeObserver' }, window.vevetApp.props.resizeDebounce + resizeDebounce);
        });
        if (Array.isArray(element)) {
            element.forEach((item) => resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.observe(item));
        }
        else {
            resizeObserver.observe(element);
        }
    }
    if (hasBothEvents || !resizeObserver) {
        viewportCallback = window.vevetApp.viewport.add(viewportTarget, () => debounceResize({ trigger: 'Viewport' }));
    }
    return {
        remove: () => {
            if (timeout) {
                clearTimeout(timeout);
            }
            resizeObserver === null || resizeObserver === void 0 ? void 0 : resizeObserver.disconnect();
            viewportCallback === null || viewportCallback === void 0 ? void 0 : viewportCallback.remove();
        },
        resize: () => handleResize({ trigger: 'unknown' }),
        debounceResize: () => debounceResize(),
        hasResizeObserver: !!resizeObserver,
    };
}
//# sourceMappingURL=onResize.js.map